import { processQueue, type ProcessQueueResult } from './queue';
import { processVoiceNoteQueue, type ProcessVoiceNoteQueueResult } from './voice-queue';
import { supportsBackgroundSync } from '../platform';

const SYNC_TAG = 'smart-capture-queue-flush';

/**
 * Wires the offline queue to reconnection. Two paths, because iOS WebKit
 * supports neither the Background Sync API nor background execution
 * (src/lib/platform.ts): a foreground `online` listener that works
 * everywhere the app is open, and a best-effort Background Sync
 * registration that only does anything on browsers that implement it.
 * Neither path silently drops a queued capture; without both, the
 * capture still uploads the next time the app is opened online, because
 * the queue itself (IndexedDB) is what survives, not this listener.
 *
 * The voice-note queue drains second, after captures: a note recorded
 * against a capture that had not uploaded yet resolves its `captureId`
 * off the same `queue` row (`voice-queue.ts` `resolveCaptureId`), which
 * only exists once the capture drain above has run.
 */
export function registerOfflineSync(
  onProcessed?: (result: ProcessQueueResult) => void,
  onVoiceNotesProcessed?: (result: ProcessVoiceNoteQueueResult) => void,
): () => void {
  const flush = () => {
    void processQueue()
      .then((result) => {
        onProcessed?.(result);
        return processVoiceNoteQueue();
      })
      .then((result) => onVoiceNotesProcessed?.(result));
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', flush);
    if (navigator.onLine) flush();
  }

  if (supportsBackgroundSync()) {
    void navigator.serviceWorker.ready
      .then((registration) => {
        const syncManager = (registration as unknown as { sync?: { register: (tag: string) => Promise<void> } }).sync;
        return syncManager?.register(SYNC_TAG);
      })
      .catch(() => {
        // Registration failing is not fatal: the foreground listener above
        // still covers every platform, including the ones where this fails.
      });
  }

  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', flush);
    }
  };
}
